import { Component, inject, ViewChild, effect } from '@angular/core';
import { CurrencyService } from './currency.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DynamicForm } from '@/components/form-builder/form-builder.types';
import { getCurrencyFormObject } from './utils/currency-form-object';
import { CurrencyRepository } from '@/store/currency-state/currency-state.repository';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { getCurrencyUpdateDrawerObject } from './utils/currency-update-drawer-object';
import { getCurrencyDeleteObject } from './utils/currency-delete-dialog-object';
import { Currency } from '@/types';
import { getCurrencyDataTableStructure } from './utils/currency-data-table-object';
import { getCurrencyAddDrawerObject } from './utils/currency-add-drawer-object';
import { currencySchema } from '@/validations/currency.validation';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { Router } from '@angular/router';
import { Button } from 'primeng/button';

@Component({
  selector: 'sm-currency',
  imports: [PageHeaderComponent, DataTableBuilderComponent, DrawerComponent, DialogComponent, TranslateModule, Button],
  templateUrl: './currency.component.html',
  styleUrl: './currency.component.scss',
})
export class CurrencyComponent {
  currencyService = inject(CurrencyService);
  currencyRepository = inject(CurrencyRepository);
  translate = inject(TranslateService);
  messageService = inject(MessageService);
  router = inject(Router);

  breadcrumb = [
    {
      label: 'settings.currency.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.currency.breadcrumb.currency',
      routerLink: ['/settings', 'currency'],
    },
  ];

  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  currencyDrawer?: DrawerObject;

  //dialog
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  currencyDialog?: DialogObject;

  formObject: DynamicForm = getCurrencyFormObject({
    store: this.currencyRepository,
  });

  rowActionsFn: (target: Currency) => DataTableRowActions = (target: Currency) => {
    return {
      editAction: () => {
        this.currencyDrawer = getCurrencyUpdateDrawerObject({
          store: this.currencyRepository,
          formObject: this.formObject,
          action: () => this.editCurrency(this.currencyRepository.getNested('response', 'id'), this.currencyRepository.getNested('dto')),
          toggleVisibility: () => this.drawerComponent.toggleVisible(),
        });
        this.drawerComponent.toggleVisible();
      },
      deleteAction: () => {
        this.currencyDialog = getCurrencyDeleteObject({
          t: this.translate,
          store: this.currencyRepository,
          action: () => {
            this.deleteCurrency(this.currencyRepository.getNested('response', 'id'));
            this.dialogComponent.toggleVisible();
          },
          toggleVisiblity: () => this.dialogComponent.toggleVisible(),
        });
        this.dialogComponent.toggleVisible();
      },
    };
  };

  data: Currency[] = [];

  // Server pagination
  serverQuery = createServerQuery({});

  dataTableObject: DynamicDataTable = getCurrencyDataTableStructure({
    store: this.currencyRepository,
    createAction: () => {
      this.currencyDrawer = getCurrencyAddDrawerObject({
        store: this.currencyRepository,
        formObject: this.formObject,
        action: () => this.addCurrency(),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    rowActionsFn: this.rowActionsFn,
    enableServerActions: true,
    serverQuery: this.serverQuery,
  });

  totalRecords = 0;
  loading = true;
  private lastQuerySignature = '';

  constructor() {
    effect(() => {
      const page = this.serverQuery.page();
      const size = this.serverQuery.pageSize();
      const sortBy = this.serverQuery.sortBy();
      const sortOrder = this.serverQuery.sortOrder();
      const search = this.serverQuery.search();
      const sort = sortBy ? `${sortBy},${sortOrder}` : '';
      const signature = JSON.stringify({ page, size, sort, search });
      if (signature !== this.lastQuerySignature) {
        this.lastQuerySignature = signature;
        this.loadData(page, size, sort, search);
      }
    });
  }

  loadData(page = 0, size = 10, sort?: string, search?: string) {
    this.loading = true;
    this.currencyService.getAllPageable({ page, size, sort, search }).subscribe({
      next: data => {
        this.totalRecords = data.content.length;
        this.data = data.content;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.currency.message.error.loading'),
        });
      },
    });
  }

  addCurrency() {
    const result = currencySchema.safeParse(this.currencyRepository.get('dto'));
    if (result.success) {
      this.currencyService.add(this.currencyRepository.get('dto')).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(this.serverQuery.page(), this.serverQuery.pageSize(), this.serverQuery.sortOrder(), this.serverQuery.search());
          this.currencyRepository.reset();
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.currency.message.success.added'),
          });
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.currency.message.error.added'),
          });
        },
      });
    } else {
      this.currencyRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  editCurrency(id: number, currency: Currency) {
    const result = currencySchema.safeParse(this.currencyRepository.get('dto'));
    if (result.success) {
      this.currencyService.update(id, { code: currency.code, description: currency.description, taux: currency.taux }).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(this.serverQuery.page(), this.serverQuery.pageSize(), this.serverQuery.sortOrder(), this.serverQuery.search());
          this.currencyRepository.reset();
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.currency.message.success.edited'),
          });
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.currency.message.error.edited'),
          });
        },
      });
    } else {
      this.currencyRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  deleteCurrency(id: number) {
    this.currencyService.delete(id).subscribe({
      next: () => {
        this.loadData(this.serverQuery.page(), this.serverQuery.pageSize(), this.serverQuery.sortOrder(), this.serverQuery.search());
        this.currencyRepository.reset();
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.currency.message.success.deleted'),
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.currency.message.error.deleted'),
        });
      },
    });
  }
}
